// calculator source: https://github.com/blevic/nrarfcn/tree/main
import { ETechnologiesValues } from '@enums/common';

import { FREQUENCY_DECIMALS, TFrequencyConverterResult } from './earfcnConverter';

const KHZ_TO_MHZ = 1000.0;

export class ConverterT5G {
	static readonly NRARFCN_MAX = 3_279_165;
	static readonly NRARFCN_MIN = 0;

	static readonly FREQ_MAX = 99_999.96;
	static readonly FREQ_MIN = 0;

	private static readonly LOW_BAND_MAX_NRARFCN = 599_999;
	private static readonly MID_BAND_MAX_NRARFCN = 2_016_666;

	private static readonly LOW_BAND_F_OFFSET = 0;
	private static readonly MID_BAND_F_OFFSET = 3000.0 * KHZ_TO_MHZ;
	private static readonly HIGH_BAND_F_OFFSET = 24_250.08 * KHZ_TO_MHZ;

	private static readonly LOW_BAND_DELTA_F = 5.0;
	private static readonly MID_BAND_DELTA_F = 15.0;
	private static readonly HIGH_BAND_DELTA_F = 60.0;

	private static readonly LOW_BAND_N_OFFSET = 0;
	private static readonly MID_BAND_N_OFFSET = 600_000.0;
	private static readonly HIGH_BAND_N_OFFSET = 2_016_667.0;

	static nrarfcnToFreq(nrarfcn: number): TFrequencyConverterResult {
		if (nrarfcn < ConverterT5G.NRARFCN_MIN || nrarfcn > ConverterT5G.NRARFCN_MAX) {
			return { frequency: NaN, bandType: '' };
		}

		let fOffset, deltaF, nOffset;

		if (nrarfcn <= ConverterT5G.LOW_BAND_MAX_NRARFCN) {
			fOffset = ConverterT5G.LOW_BAND_F_OFFSET;
			deltaF = ConverterT5G.LOW_BAND_DELTA_F;
			nOffset = ConverterT5G.LOW_BAND_N_OFFSET;
		} else if (nrarfcn <= ConverterT5G.MID_BAND_MAX_NRARFCN) {
			fOffset = ConverterT5G.MID_BAND_F_OFFSET;
			deltaF = ConverterT5G.MID_BAND_DELTA_F;
			nOffset = ConverterT5G.MID_BAND_N_OFFSET;
		} else {
			fOffset = ConverterT5G.HIGH_BAND_F_OFFSET;
			deltaF = ConverterT5G.HIGH_BAND_DELTA_F;
			nOffset = ConverterT5G.HIGH_BAND_N_OFFSET;
		}

		const freqKHz = fOffset + deltaF * (nrarfcn - nOffset);
		const freqMHz = freqKHz / KHZ_TO_MHZ;

		return {
			frequency: +(Math.round(freqMHz * KHZ_TO_MHZ) / KHZ_TO_MHZ).toFixed(FREQUENCY_DECIMALS),
			bandType: ETechnologiesValues.T_5G,
		};
	}

	static freqToNrarfcn(freq: number): number {
		if (freq < ConverterT5G.FREQ_MIN || freq > ConverterT5G.FREQ_MAX) {
			return NaN;
		}

		let fOffset, deltaF, nOffset;

		if (freq <= ConverterT5G.MID_BAND_F_OFFSET / KHZ_TO_MHZ) {
			fOffset = ConverterT5G.LOW_BAND_F_OFFSET;
			deltaF = ConverterT5G.LOW_BAND_DELTA_F;
			nOffset = ConverterT5G.LOW_BAND_N_OFFSET;
		} else if (freq <= ConverterT5G.HIGH_BAND_F_OFFSET / KHZ_TO_MHZ) {
			fOffset = ConverterT5G.MID_BAND_F_OFFSET / KHZ_TO_MHZ;
			deltaF = ConverterT5G.MID_BAND_DELTA_F;
			nOffset = ConverterT5G.MID_BAND_N_OFFSET;
		} else {
			fOffset = ConverterT5G.HIGH_BAND_F_OFFSET / KHZ_TO_MHZ;
			deltaF = ConverterT5G.HIGH_BAND_DELTA_F;
			nOffset = ConverterT5G.HIGH_BAND_N_OFFSET;
		}

		const nrarfcn = nOffset + (freq - fOffset) * KHZ_TO_MHZ / deltaF;

		return Math.min(Math.round(nrarfcn), ConverterT5G.NRARFCN_MAX);
	}
}
